import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { SurveyContext } from '../contexts/SurveyProvider'
import useFilteredMultiSelect from '../hooks/useFilteredMultiSelect'
import { filterQuestions } from './VizUI'
import './GrouperUI.css'
import { NUMERIC_VARIABLE_TYPES } from './variables/VariablesUI'
import { useApi } from '../contexts/ApiProvider'
import { AppToaster } from './toaster'
import { errorObjectToString } from '../ApiClient'
import InputSelectionPanel from './grouper/InputSelectionPanel'
import SegmentationConfigPanel from './grouper/SegmentationConfigPanel'
import { allAlgorithms } from './grouper/AlgorithmSelector'

export default function GrouperUI() {
  const { variables } = useContext(SurveyContext)
  const [segSizes, setSegSizes] = useState([3, 7])
  const [algorithmsSelected, setAlgorithmsSelected] = useState(allAlgorithms)
  const [segName, setSegName] = useState('')
  const seedInputRef = useRef()
  const nmfRunsInputRef = useRef()
  const snmfUseHardAssignmentRef = useRef()
  const snmfBalanceToleranceRef = useRef()
  const numericVariables = useMemo(
    () =>
      variables.filter(v => NUMERIC_VARIABLE_TYPES.includes(v.variable_type)),
    [variables]
  )
  const [
    { selected, query, filteredSelection, filteredOptions },
    { select, reset, queryChange, set },
  ] = useFilteredMultiSelect(
    { selected: [], query: '' },
    filterQuestions,
    numericVariables
  )
  // Clean up stale selected inputs when variables change (e.g. after segmentation deletion)
  useEffect(() => {
    if (!numericVariables) return
    const validIds = new Set(numericVariables.map(v => v.id))
    set(prev => prev.filter(v => validIds.has(v.id)))    
  }, [numericVariables])

  const [loading, setLoading] = useState(false)
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false)
  const validInputs = selected.length && selected.length >= segSizes[1]
  const api = useApi()

  const handleSubmitSegmentation = async ev => {
    ev.preventDefault()
    setLoading(true)
    AppToaster.clear()
    const useHardAssignment = snmfUseHardAssignmentRef.current?.checked ?? true

    // Get balance tolerance: use ref value if present, otherwise default to 30% for hard assignment
    let balanceTolerance = null
    if (useHardAssignment) {
      const refValue = snmfBalanceToleranceRef.current?.value
      balanceTolerance = refValue ? parseFloat(refValue) / 100 : 0.30
    }

    const data = {
      sizes: segSizes,
      algos: algorithmsSelected,
      inputs: selected.map(v => v.id),
      name: segName,
      seed: seedInputRef.current ? parseInt(seedInputRef.current.value) : 0,
      nmf_runs: nmfRunsInputRef.current ? parseInt(nmfRunsInputRef.current.value) : 40,
      snmf_balance_tolerance: balanceTolerance,
    }

    try {
      const response = await api.post('/segment', data)
      console.log(response)
      if (response.ok) {
        AppToaster.show({
          message:
            "Segmentation has been queued. You'll receive notifications about its progress.",
          intent: 'success',
          icon: 'tick-circle',
        })
      } else {
        const message = response.body?.messages?.json
          ? errorObjectToString(response.body.messages.json)
          : 'An error occurred while submitting the segmentation'
        AppToaster.show({
          message: message,
          intent: 'danger',
          icon: 'error',
          timeout: 0,
        })
      }
    } catch (error) {
      console.error('Error submitting segmentation:', error)
      AppToaster.show({
        message: 'Failed to submit segmentation. Please try again.',
        intent: 'danger',
        icon: 'error',
        timeout: 0,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        padding: '20px',
        height: '100%',
      }}
    >
      <form
        onSubmit={handleSubmitSegmentation}
        style={{
          display: 'flex',
          gap: '20px',
          height: '100%',
        }}
      >
        <div style={{ flex: '1.5 1 0', minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <InputSelectionPanel
            query={query}
            queryChange={queryChange}
            filteredOptions={filteredOptions}
            numericVariables={numericVariables}
            filteredSelection={filteredSelection}
            selected={selected}
            set={set}
            segSizes={segSizes}
            reset={reset}
          />
        </div>
        <div style={{ flex: '1 1 0', minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <SegmentationConfigPanel
            algorithmsSelected={algorithmsSelected}
            setAlgorithmsSelected={setAlgorithmsSelected}
            segSizes={segSizes}
            setSegSizes={setSegSizes}
            selected={selected}
            segName={segName}
            setSegName={setSegName}
            validInputs={validInputs}
            loading={loading}
            advancedSettingsOpen={advancedSettingsOpen}
            setAdvancedSettingsOpen={setAdvancedSettingsOpen}
            seedInputRef={seedInputRef}
            nmfRunsInputRef={nmfRunsInputRef}
            snmfUseHardAssignmentRef={snmfUseHardAssignmentRef}
            snmfBalanceToleranceRef={snmfBalanceToleranceRef}
          />
        </div>
      </form>
    </div>
  )
}
